import graphql from "babel-plugin-relay/macro";
import { Suspense, useCallback, useEffect } from "react";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { packageIsAvailableForSale } from "@whoosh/features/src/Packages";

import { StorePackageModal } from "./StorePackageModal";
import { StoreLinkedPackageModalQuery } from "./__generated__/StoreLinkedPackageModalQuery.graphql";
import { StoreLinkedPackageModal_query$key } from "./__generated__/StoreLinkedPackageModal_query.graphql";

type Props = {
  packageId: string;
  type: "MEMBERSHIP" | "STANDARD";
  onClose: () => void;
};

export const StoreLinkedPackageModal = ({
  packageId,
  type,
  onClose,
}: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<StoreLinkedPackageModalQuery>(
    StoreLinkedPackageModalQueryString
  );

  const loadPackage = useCallback(() => {
    loadQuery({ packageId }, { fetchPolicy: "store-and-network" });
  }, [loadQuery, packageId]);

  useEffect(() => {
    loadPackage();
  }, [loadPackage]);

  return (
    <Suspense fallback={null}>
      {queryRef ? (
        <StoreLinkedPackageModalInner
          queryRef={queryRef}
          type={type}
          onClose={onClose}
          onAuthComplete={loadPackage}
        />
      ) : null}
    </Suspense>
  );
};

type InnerProps = {
  type: "MEMBERSHIP" | "STANDARD";
  queryRef: PreloadedQuery<StoreLinkedPackageModalQuery>;
  onClose: () => void;
  onAuthComplete: () => void;
};

const StoreLinkedPackageModalInner = ({
  queryRef,
  type,
  onClose,
  onAuthComplete,
}: InnerProps) => {
  const data = usePreloadedQuery(StoreLinkedPackageModalQueryString, queryRef);

  return (
    <StoreLinkedPackageModalContent
      queryRef={data}
      type={type}
      onClose={onClose}
      onAuthComplete={onAuthComplete}
    />
  );
};

type ContentProps = {
  type: "MEMBERSHIP" | "STANDARD";
  queryRef: StoreLinkedPackageModal_query$key;
  onClose: () => void;
  onAuthComplete: () => void;
};

export const StoreLinkedPackageModalContent = ({
  queryRef,
  type,
  onClose,
  onAuthComplete,
}: ContentProps) => {
  const data = useFragment(StoreLinkedPackageModalFragmentString, queryRef);
  const pkg = data.package;
  const isPackage =
    pkg?.__typename === "MembershipPackage" ||
    pkg?.__typename === "StandardPackage";
  const isSaleEnabled =
    isPackage &&
    pkg.__typename ===
      (type === "MEMBERSHIP" ? "MembershipPackage" : "StandardPackage") &&
    (pkg.storeAvailability === "PUBLIC" || pkg.storeAvailability === "PRIVATE");
  const isAvailable =
    isSaleEnabled &&
    pkg.assignable &&
    packageIsAvailableForSale(pkg, pkg.club.timeZone);

  useEffect(() => {
    if (!isAvailable) {
      onClose();
    }
  }, [isAvailable, onClose]);

  return isAvailable ? (
    <StorePackageModal
      packageRef={pkg}
      sessionRef={data.session}
      isOpen
      onClose={onClose}
      onAuthComplete={onAuthComplete}
    />
  ) : null;
};

const StoreLinkedPackageModalQueryString = graphql`
  query StoreLinkedPackageModalQuery($packageId: ID!) {
    ...StoreLinkedPackageModal_query @arguments(packageId: $packageId)
  }
`;

const StoreLinkedPackageModalFragmentString = graphql`
  fragment StoreLinkedPackageModal_query on RootQueryType
  @argumentDefinitions(packageId: { type: "ID!" }) {
    session {
      ...StorePackageModal_session
    }
    package: node(id: $packageId) {
      __typename
      ... on MembershipPackage {
        assignable
        storeAvailability
        club {
          timeZone
        }
        ...packageIsAvailableForSale_package
        ...StorePackageModal_package
      }
      ... on StandardPackage {
        assignable
        storeAvailability
        club {
          timeZone
        }
        ...packageIsAvailableForSale_package
        ...StorePackageModal_package
      }
    }
  }
`;
